// Saving and sharing the badge story. The PNG is rendered ahead of time by the
// share page, so the Share tap can call navigator.share straight away (Safari
// drops the user gesture if the page awaits a render first).

export type ShareOutcome = "shared-file" | "shared-link" | "copied" | "cancelled" | "failed";

export interface ShareStoryInput {
  /** The story PNG; without it the link is shared. */
  blob: Blob | null;
  filename: string;
  url: string;
  title: string;
  text: string;
}

type ShareNavigator = Navigator & {
  canShare?: (data?: ShareData) => boolean;
  share?: (data?: ShareData) => Promise<void>;
};

function isAbort(err: unknown): boolean {
  return !!err && typeof err === "object" && (err as { name?: string }).name === "AbortError";
}

/**
 * Share the story: the PNG file where the browser can share files, otherwise the
 * link through the share sheet, otherwise copy the link.
 */
export async function shareStory(input: ShareStoryInput): Promise<ShareOutcome> {
  const nav = (typeof navigator !== "undefined" ? navigator : undefined) as ShareNavigator | undefined;
  if (!nav) return "failed";

  if (input.blob && typeof File !== "undefined" && nav.share && nav.canShare) {
    const file = new File([input.blob], input.filename, { type: "image/png" });
    let canShareFile = false;
    try {
      canShareFile = nav.canShare({ files: [file] });
    } catch {
      canShareFile = false;
    }
    if (canShareFile) {
      try {
        await nav.share({ files: [file], title: input.title, text: input.text });
        return "shared-file";
      } catch (err) {
        if (isAbort(err)) return "cancelled";
        // fall through to the link
      }
    }
  }

  if (nav.share) {
    try {
      await nav.share({ title: input.title, text: input.text, url: input.url });
      return "shared-link";
    } catch (err) {
      if (isAbort(err)) return "cancelled";
    }
  }

  try {
    await nav.clipboard.writeText(input.url);
    return "copied";
  } catch {
    return "failed";
  }
}

/** Download a blob as a file (the "Save image" button). */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
